import { type PropsWithChildren, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button as ZusButton, Chip as ZusChip, Purple50, White } from '@zep-us/zus';
import { BoltFillIcon, BoltSmallRectangleColorMintIcon, BoltSmallRectangleColorVioletIcon, CalendarIcon, CheckIcon, ChevronRightIcon, PersonStackIcon, SchoolFillIcon } from '@zep-us/zus-icons';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import '../tracking/events.quiz-school-pricing';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { useGetUserPlanAvailability } from '@/api/gen/quiz-plan-payment/quiz-plan-payment.gen';
import { useLocale, useOpenWindow } from '@/hooks';
import { BoltFillIcon as LegacyBoltFillIcon, BoltSmallRectangleColorVioletIcon as LegacyBoltSmallRectangleColorVioletIcon, CalendarIcon as LegacyCalendarIcon, CheckSmallIcon, ChevronDownIcon, PaperIcon, PersonStackIcon as LegacyPersonStackIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { usePrices } from '@/module/payment/hooks';
import { getInquiryLinkByCountryCode } from '@/module/plan/consts/inquiryLinks';
import { BasicPricingCard, FreePricingCard, PricingCard, ProPricingCard } from '@/shared/components/PricingCard';
import { RestrictedPlanDialog } from '@/shared/components/UpsellingModal/RestrictedPlanDialog';
import { resolveUpsellingPlanState } from '@/shared/components/UpsellingModal/upsellingPlanState';
import { getUpsellingCardCta, type PaidPlanType, type UpsellingCtaAction } from '@/shared/components/UpsellingModal/upsellingPolicy';
import { useCountryCode, useInquiryUrl } from '@/shared/hooks';
import { ALL_QUIZ_TYPES_FEATURE_COPY } from '@/shared/plan/consts';
import { formatCurrencySymbol } from '@/shared/utils/currency';
import type { UserInfo } from '@/types';
import { Button } from '@/ui_v3';
import { combinePathAndQuery } from '@/utils/combinePathAndQuery';
import { cn } from '@/utils/tailwind.util';
import { useCurrentPlanChangeGuard, usePaymentFlow } from '../../shared';
import { getPaymentLoginUrl } from '../../shared/utils/paymentEntryUrl';
import { calculateGroupPricingPlanMetrics, getCurrencyForCountry, getMaximumGroupDiscount, type GroupPricingContent, type GroupPricingPlan, type GroupPricingPlanMetrics } from './groupPricingContent';
import { IndividualPlanCardsSkeleton } from './IndividualPlanCardsSkeleton';
import { DEFAULT_PRICING_TAB, getDefaultPricingTab, parsePricingTab, PRICING_TAB_QUERY_KEY, type PricingTab } from './pricingTabQuery';
const HERO_BODY_CLASS = 'flex flex-col gap-3xl items-center px-md pb-3xl pt-2xl w-full max-w-[1440px] tablet-sm:px-2xl laptop-lg:px-[80px]';
const INDIVIDUAL_PLAN_CARDS_CLASS = 'flex w-full max-w-[1280px] flex-col items-center gap-lg tablet-lg:flex-row tablet-lg:items-stretch tablet-lg:justify-center';

// 개인/학교·단체 탭의 좌→우 순서. 콘텐츠 슬라이드 방향을 계산하는 데 사용.
const TAB_ORDER: Record<PricingTab, number> = {
  individual: 0,
  group: 1
};
const TAB_SLIDE_DISTANCE = 24;

// 겹침 크로스페이드: 들어오는 콘텐츠는 방향(d)쪽에서 밀려들고, 나가는 콘텐츠는 반대로 빠진다.
// 나가는(exit) 레이어는 pointer-events를 꺼서 겹쳐 있어도 아래 버튼 클릭을 막지 않는다.
const tabContentVariants = {
  enter: (d: number) => ({
    x: d * TAB_SLIDE_DISTANCE,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (d: number) => ({
    x: d * -TAB_SLIDE_DISTANCE,
    opacity: 0,
    pointerEvents: 'none' as const
  })
};

// 상단 탭 활성 표시(흰 카드)가 개인↔학교 사이를 미끄러질 때 공유되는 레이아웃 ID.
const TAB_INDICATOR_LAYOUT_ID = 'pricing-tab-active-surface';

// 하단 구분선을 덮는 흰 커넥터도 본체와 함께 슬라이드시키기 위한 레이아웃 ID.
const TAB_CONNECTOR_LAYOUT_ID = 'pricing-tab-connector';

// 모바일은 각 탭이 최소 158px을 유지하고, 긴 번역은 탭당 최대 200px까지 확장한다.
// 498px 이상에서는 각 탭이 최소 247px을 유지한다.
const PRICING_TAB_GRID_CLASS = 'w-fit min-w-[316px] max-w-[min(400px,calc(100vw-32px))] grid-cols-[repeat(2,minmax(158px,1fr))] min-[498px]:w-auto min-[498px]:min-w-0 min-[498px]:max-w-none min-[498px]:grid-cols-[repeat(2,minmax(247px,1fr))]';
const PRICING_TAB_BUTTON_CLASS = 'relative z-10 flex h-full min-w-0 flex-col items-center justify-center px-md pb-xs pt-sm tablet-sm:px-2xl tablet-sm:pb-sm tablet-sm:pt-md';
const PRICING_TAB_TEXT_CLASS = 'relative w-full text-center text-heading-sm-bold tablet-sm:text-heading-lg-extrabold';
const HEADER_BG = ['linear-gradient(180deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0) 100%)', 'linear-gradient(89.99deg, rgba(213, 216, 252, 0.25) 0%, rgba(185, 239, 233, 0.25) 100%)', 'linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)'].join(', ');
const GROUP_HERO_BACKGROUND = `linear-gradient(180deg, ${White} 0%, ${Purple50} 30%, ${Purple50} 50.282%, ${Purple50} 70%, ${White} 100%)`;
const BUDGET_TAGS = ['#디지털 선도학교', '#AI 중점학교 운영', '#학습준비물', '#에듀테크/디지털교육/특수교육', '#늘봄학교 운영', '#교육운영비', '#교육과정 자율운영 예산', '#학생 참여형 수업'];
const KOREAN_SCHOOL_PLAN_FEATURES = ['pricing:schoolGroup.features.unlimitedMaps', ALL_QUIZ_TYPES_FEATURE_COPY.key, 'pricing:schoolGroup.features.allLearningReportFeatures', 'pricing:schoolGroup.features.continueNextQuiz', 'pricing:schoolGroup.features.worksheetSave'] as const;
const KOREAN_SCHOOL_PLAN_AI_FEATURES = ['pricing:schoolGroup.features.aiTutor', 'pricing:schoolGroup.features.aiTranslation', 'pricing:schoolGroup.features.similarQuiz', 'pricing:schoolGroup.features.choiceGeneration'] as const;
export const GroupPricingSection = ({
  content,
  hasSessionHint
}: {
  content: GroupPricingContent;
  hasSessionHint: boolean;
}) => {
  const router = useRouter();
  const openWindow = useOpenWindow();
  const {
    locale
  } = useLocale();
  const shouldReduceMotion = useReducedMotion();
  const basicIndividualPrices = usePrices({
    countryCode: content.countryCode,
    planType: 'BASIC'
  });
  const proIndividualPrices = usePrices({
    countryCode: content.countryCode,
    planType: 'PRO'
  });
  const basicMetrics = calculateGroupPricingPlanMetrics(content.basic, content.averageTeacherCount, basicIndividualPrices.displayOriginalPrice);
  const proMetrics = calculateGroupPricingPlanMetrics(content.pro, content.averageTeacherCount, proIndividualPrices.displayOriginalPrice);
  const groupDiscountPercent = getMaximumGroupDiscount(basicMetrics, proMetrics);

  // 활성 탭은 로컬 state가 소스 — 클릭 즉시 애니메이션이 반영되도록(라우터 갱신을 기다리지 않음).
  const defaultTab = getDefaultPricingTab(content.countryCode);
  const queryTab = parsePricingTab(router.query[PRICING_TAB_QUERY_KEY], defaultTab);
  const [activeTab, setActiveTab] = useState<PricingTab>(queryTab);

  // 탭 전환 애니메이션은 사용자가 실제로 탭을 바꿀 때만 재생한다.
  // 첫 진입(URL 기반)·초기 너비 측정으로 인한 레이아웃 정착은 애니메이션 없이 스냅.
  const [tabMotionEnabled, setTabMotionEnabled] = useState(false);

  // URL → state 동기화: 뒤로가기/앞으로가기·직접 진입·쿼리 준비(hydration) 시 반영.
  useEffect(() => {
    setActiveTab(queryTab);
  }, [queryTab]);

  // 콘텐츠 슬라이드 방향: 오른쪽 탭으로 가면 +1, 왼쪽 탭으로 가면 -1.
  const prevTabIndexRef = useRef(TAB_ORDER[activeTab]);
  const direction = TAB_ORDER[activeTab] >= prevTabIndexRef.current ? 1 : -1;
  useEffect(() => {
    prevTabIndexRef.current = TAB_ORDER[activeTab];
  }, [activeTab]);
  const handleQuoteClick = useCallback(() => {
    logger.track('click_get_quote_button');
    // 진입 세그먼트를 견적 폼에 전달해 기본값을 프리필한다(개인/학교·단체).
    const quotePath = activeTab === DEFAULT_PRICING_TAB ? '/pricing/quote' : `/pricing/quote?${PRICING_TAB_QUERY_KEY}=${activeTab}`;
    openWindow(quotePath);
  }, [openWindow, activeTab]);
  const handleTabChange = useCallback((tab: PricingTab) => {
    trackEvent('quiz_pricing_tab_click', {
      tab,
      previous_tab: activeTab
    });

    // 사용자 클릭이므로 이 시점부터 탭 전환 애니메이션을 켠다.
    setTabMotionEnabled(true);
    // 애니메이션 즉시 반영 → 라우터 갱신은 URL 동기화(딥링크/공유)용으로 병행.
    setActiveTab(tab);
    const nextQuery = {
      ...router.query
    };
    if (tab === defaultTab) {
      delete nextQuery[PRICING_TAB_QUERY_KEY];
    } else {
      nextQuery[PRICING_TAB_QUERY_KEY] = tab;
    }
    void router.replace({
      pathname: router.pathname,
      query: nextQuery
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }, [activeTab, defaultTab, router]);
  return <section className="relative left-1/2 flex w-screen -translate-x-1/2 flex-col items-center bg-default" data-sentry-component="GroupPricingSection" data-sentry-source-file="GroupPricingSection.tsx">
      <HeroHeader activeTab={activeTab} groupDiscountPercent={groupDiscountPercent} motionEnabled={tabMotionEnabled} onTabChange={handleTabChange} onQuoteClick={handleQuoteClick} data-sentry-element="HeroHeader" data-sentry-source-file="GroupPricingSection.tsx" />

      <div className="relative flex w-full flex-col items-center overflow-hidden" style={{
      backgroundImage: GROUP_HERO_BACKGROUND
    }}>
        <AnimatePresence mode="popLayout" initial={false} custom={direction} data-sentry-element="AnimatePresence" data-sentry-source-file="GroupPricingSection.tsx">
          <motion.div key={activeTab} custom={direction} variants={tabContentVariants} initial="enter" animate="center" exit="exit" transition={!tabMotionEnabled || shouldReduceMotion ? {
          duration: 0
        } : {
          duration: 0.28,
          ease: [0.4, 0, 0.2, 1]
        }} className={HERO_BODY_CLASS} data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx">
            {activeTab === 'group' ? content.countryCode === 'KR' ? <KoreanSchoolTabContent /> : <GlobalSchoolTabContent basicCurrency={basicIndividualPrices.displayCurrency || getCurrencyForCountry(content.countryCode)} basicMetrics={basicMetrics} content={content} proCurrency={proIndividualPrices.displayCurrency || getCurrencyForCountry(content.countryCode)} proMetrics={proMetrics} /> : <>
                <IndividualPlanCards hasSessionHint={hasSessionHint} />
                {content.countryCode === 'KR' && locale === 'ko' && <SchoolBudgetCallout />}
              </>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
};
const HeroHeader = ({
  activeTab,
  groupDiscountPercent,
  motionEnabled,
  onTabChange,
  onQuoteClick
}: {
  activeTab: PricingTab;
  groupDiscountPercent?: number;
  motionEnabled: boolean;
  onTabChange: (tab: PricingTab) => void;
  onQuoteClick: () => void;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const {
    isKR
  } = useCountryCode();
  const individualLabel = t('pricing:schoolGroup.individual');
  const groupLabel = t('pricing:schoolGroup.schoolGroup');
  const mobileGroupLabel = t('pricing:schoolGroup.school');
  const discountLabel = groupDiscountPercent ? t('pricing:groupPricing.default.discount', {
    discount: groupDiscountPercent
  }) : undefined;
  return <div className="flex w-full flex-col items-center gap-3xl pt-3xl" style={{
    backgroundImage: HEADER_BG
  }} data-sentry-component="HeroHeader" data-sentry-source-file="GroupPricingSection.tsx">
      <div className="flex w-full flex-col items-center gap-2xl">
        <div className="flex flex-col items-center gap-lg">
          <div className="flex w-full max-w-[1280px] items-center justify-center">
            <h1 className="max-w-[calc(100vw-32px)] whitespace-pre-line break-keep text-center text-[clamp(24px,8vw,30px)] font-extrabold leading-[1.3] text-default tablet-sm:text-[40px] tablet-sm:leading-[52px]">
              <Trans t={t} i18nKey="common:pricing.enhanceClassWithMoreFeatures" components={{
              Gradient: <span className="bg-clip-text text-transparent bg-gradient-violet-blue" />
            }} data-sentry-element="Trans" data-sentry-source-file="GroupPricingSection.tsx" />
            </h1>
          </div>

          {isKR && <ZusButton variant="contained" intent="strong" size="lg" rounded="full" leftSlot={<PaperIcon size="sm" />} className="max-w-[calc(100vw-32px)]" onClick={onQuoteClick}>
              {t('common:pricing.getQuoteInMinute')}
            </ZusButton>}
        </div>

        <div className="relative flex w-full flex-col items-center">
          <div className="absolute bottom-0 left-0 right-0 h-px bg-alternative" />
          <div className="flex w-full max-w-[1280px] flex-col items-center justify-end pt-md">
            <div role="group" aria-label={t('common:pricing.planGuide')} className={cn('relative grid items-stretch justify-center', PRICING_TAB_GRID_CLASS)}>
              <PricingTabButton active={activeTab === 'individual'} motionEnabled={motionEnabled} onClick={() => onTabChange('individual')} data-sentry-element="PricingTabButton" data-sentry-source-file="GroupPricingSection.tsx">
                {individualLabel}
              </PricingTabButton>
              <PricingTabButton active={activeTab === 'group'} ariaLabel={[groupLabel, discountLabel].filter(Boolean).join(' ')} motionEnabled={motionEnabled} onClick={() => onTabChange('group')} data-sentry-element="PricingTabButton" data-sentry-source-file="GroupPricingSection.tsx">
                <GroupTabLabel groupLabel={groupLabel} mobileGroupLabel={mobileGroupLabel} discountLabel={discountLabel} data-sentry-element="GroupTabLabel" data-sentry-source-file="GroupPricingSection.tsx" />
              </PricingTabButton>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
const PricingTabButton = ({
  active,
  ariaLabel,
  children,
  motionEnabled,
  onClick
}: PropsWithChildren<{
  active: boolean;
  ariaLabel?: string;
  motionEnabled: boolean;
  onClick: () => void;
}>) => {
  const shouldReduceMotion = useReducedMotion();
  // 사용자 탭 전환만 스프링 슬라이드. 첫 진입/폰트 로드 등 비사용자 변경은 스냅.
  const indicatorTransition = !motionEnabled || shouldReduceMotion ? {
    duration: 0
  } : {
    type: 'spring' as const,
    stiffness: 500,
    damping: 40
  };
  return <button type="button" aria-label={ariaLabel} className={PRICING_TAB_BUTTON_CLASS} onClick={onClick} data-sentry-component="PricingTabButton" data-sentry-source-file="GroupPricingSection.tsx">
      {active && <>
          {/* 위·좌·우 보더 + 상단 라운드만 있는 탭 본체(바닥 열림). 박스에 딱 맞춰 옆 보더가 삐져나오지 않음. */}
          <motion.span layoutId={TAB_INDICATOR_LAYOUT_ID} className="absolute inset-0 -z-10 rounded-tl-large rounded-tr-large border border-b-0 border-alternative bg-default shadow-md [clip-path:inset(-16px_-16px_0px_-16px)] min-[498px]:rounded-tl-md min-[498px]:rounded-tr-md" transition={indicatorTransition} />
          {/* 보더 없는 흰 커넥터: 하단 구분선을 덮어 아래 콘텐츠와 이어지게 함(DPR 1 데스크탑 대응).
              본체와 같은 layoutId 계열로 함께 슬라이드시켜 이동 중 구분선이 드러나지 않게 한다. */}
          <motion.span aria-hidden layoutId={TAB_CONNECTOR_LAYOUT_ID} className="pointer-events-none absolute inset-x-px -bottom-px -z-10 h-[3px] bg-default" transition={indicatorTransition} />
        </>}
      <span className={cn(PRICING_TAB_TEXT_CLASS, active ? 'text-strong' : 'text-alternative')}>
        {children}
      </span>
    </button>;
};
const GroupTabLabel = ({
  groupLabel,
  mobileGroupLabel,
  discountLabel
}: {
  groupLabel: string;
  mobileGroupLabel: string;
  discountLabel?: string;
}) => {
  return <>
      <span className="flex w-full min-w-0 flex-col items-center justify-center gap-3xs min-[498px]:hidden">
        <span className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
          {mobileGroupLabel}
        </span>
        {discountLabel && <ZusChip size="xs" rounded="default" className="h-[18px] max-w-full shrink-0 border-pink-400/10 bg-pink-400/[0.15] text-pink-400">
            {discountLabel}
          </ZusChip>}
      </span>

      <span className="hidden w-full min-w-0 items-center justify-center gap-3xs whitespace-nowrap min-[498px]:flex">
        <span className="max-w-full overflow-hidden text-ellipsis">
          {groupLabel}
        </span>
        {discountLabel && <ZusChip size="md" rounded="default" className="max-w-full shrink-0 border-pink-400/10 bg-pink-400/[0.15] text-pink-400">
            {discountLabel}
          </ZusChip>}
      </span>
    </>;
};
export const shouldDeferIndividualPlanCards = ({
  hasSessionHint,
  isUserInitialized,
  isPromotionPending
}: {
  hasSessionHint: boolean;
  isUserInitialized: boolean;
  isPromotionPending: boolean;
}) => hasSessionHint && (!isUserInitialized || isPromotionPending);
export const getIndividualPricingCardCta = (user: UserInfo | null | undefined, targetPlan: PaidPlanType) => getUpsellingCardCta({
  state: resolveUpsellingPlanState(user),
  targetPlan,
  intent: 'NEW_PURCHASE'
});
const IndividualPlanCards = ({
  hasSessionHint
}: {
  hasSessionHint: boolean;
}) => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['common']);
  const {
    isAuthenticated,
    isInitialized: isUserInitialized,
    user
  } = useUser();
  const openWindow = useOpenWindow();
  const {
    countryCode
  } = useCountryCode();
  const paymentCountryCode = user?.countryCode ?? countryCode;
  const {
    startPaymentFlow
  } = usePaymentFlow();
  const planState = resolveUpsellingPlanState(user);
  const isPlanChangeBlocked = useCurrentPlanChangeGuard({
    enabled: planState.kind === 'individual'
  });
  const ctaLabels: Record<UpsellingCtaAction, string> = {
    purchase: t('common:pricing.pro.purchase'),
    'select-plan': t('common:pricing.pro.purchase'),
    extend: t('common:pricing.extendPeriod'),
    upgrade: t('common:pricing.pro.upgradeToPro'),
    'higher-plan-active': t('common:pricing.proPlanInUse'),
    'current-plan': t('common:upselling.currentlyUsing'),
    inquiry: t('common:pricing.pro.inquireNow')
  };
  const basicCta = getIndividualPricingCardCta(user, 'BASIC');
  const proCta = getIndividualPricingCardCta(user, 'PRO');
  const isFreeUser = user?.planType === 'FREE';
  const {
    isPending: isAvailabilityPending
  } = useGetUserPlanAvailability({
    query: {
      enabled: isAuthenticated && isFreeUser,
      staleTime: Infinity,
      retry: 0,
      throwOnError: false,
      meta: {
        ignoreError: true
      }
    }
  });
  const shouldDefer = shouldDeferIndividualPlanCards({
    hasSessionHint,
    isUserInitialized,
    isPromotionPending: isAuthenticated && isFreeUser && isAvailabilityPending
  });
  const handleCompareClick = () => {
    logger.track('click_compare_plan_features_button');
    document.getElementById('plan-comparison')?.scrollIntoView({
      block: 'start',
      behavior: 'smooth'
    });
  };
  const startPurchase = (plan: PaidPlanType) => {
    const entry = {
      kind: 'purchase' as const,
      plan
    };
    if (!isAuthenticated) {
      openWindow(getPaymentLoginUrl(entry, paymentCountryCode));
      return;
    }
    startPaymentFlow({
      ...entry,
      countryCode: paymentCountryCode
    }, {
      target: 'new-tab'
    });
  };
  const executeCta = (plan: PaidPlanType) => {
    const action = plan === 'BASIC' ? basicCta.action : proCta.action;
    switch (action) {
      case 'purchase':
        startPurchase(plan);
        return;
      case 'select-plan':
        overlay.open(({
          unmount
        }) => <RestrictedPlanDialog onClose={unmount} />);
        return;
      case 'extend':
        if (isPlanChangeBlocked('extend')) return;
        startPaymentFlow({
          kind: 'extend',
          target: {
            kind: 'active'
          },
          countryCode: paymentCountryCode
        }, {
          target: 'new-tab'
        });
        return;
      case 'upgrade':
        if (isPlanChangeBlocked('upgrade')) return;
        startPaymentFlow({
          kind: 'upgrade',
          target: {
            kind: 'active'
          },
          countryCode: paymentCountryCode
        }, {
          target: 'new-tab'
        });
        return;
      case 'inquiry':
        window.open(getInquiryLinkByCountryCode(paymentCountryCode), '_blank', 'noopener,noreferrer');
        return;
      case 'current-plan':
      case 'higher-plan-active':
        return;
    }
  };
  const handleCardCta = (plan: PaidPlanType) => {
    logger.track('click_pricing_card_cta', {
      // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
      planType: plan,
      from: router.pathname
    });
    executeCta(plan);
  };
  const handleSecondaryPurchase = (plan: PaidPlanType) => {
    logger.track('click_pricing_card_cta', {
      // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
      planType: plan,
      from: router.pathname
    });
    startPurchase(plan);
  };
  return <div aria-busy={shouldDefer} className={cn('relative', INDIVIDUAL_PLAN_CARDS_CLASS)} data-sentry-component="IndividualPlanCards" data-sentry-source-file="GroupPricingSection.tsx">
      <FreePricingCard className={cn(shouldDefer && 'invisible')} footer={<PlanCompareFooter onClick={handleCompareClick} />} onCtaButtonClick={() => {
      logger.track('click_pricing_card_cta', {
        // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
        planType: 'FREE',
        from: router.pathname
      });
      void router.push(isAuthenticated ? '/build' : combinePathAndQuery('/login', router.query, [PRICING_TAB_QUERY_KEY]));
    }} data-sentry-element="FreePricingCard" data-sentry-source-file="GroupPricingSection.tsx" />
      <BasicPricingCard className={cn(shouldDefer && 'invisible')} footer={<PlanCompareFooter onClick={handleCompareClick} />} ctaButtonText={ctaLabels[basicCta.action]} ctaButtonDisabled={basicCta.disabled} onCtaButtonClick={() => handleCardCta('BASIC')} secondaryCtaText={basicCta.hasSecondaryPurchase ? t('common:detail.planPurchase') : undefined} onSecondaryCtaClick={() => handleSecondaryPurchase('BASIC')} data-sentry-element="BasicPricingCard" data-sentry-source-file="GroupPricingSection.tsx" />
      <ProPricingCard className={cn(shouldDefer && 'invisible')} footer={<PlanCompareFooter onClick={handleCompareClick} />} ctaButtonText={ctaLabels[proCta.action]} ctaButtonDisabled={proCta.disabled} onCtaButtonClick={() => handleCardCta('PRO')} secondaryCtaText={proCta.hasSecondaryPurchase ? t('common:detail.planPurchase') : undefined} onSecondaryCtaClick={() => handleSecondaryPurchase('PRO')} data-sentry-element="ProPricingCard" data-sentry-source-file="GroupPricingSection.tsx" />
      {shouldDefer && <IndividualPlanCardsSkeleton className={INDIVIDUAL_PLAN_CARDS_CLASS} />}
    </div>;
};

// 각 요금제 카드 하단 "플랜별 기능 비교" 링크 → 비교표로 스크롤. KR 카드에만 전달.
const PlanCompareFooter = ({
  onClick
}: {
  onClick: () => void;
}) => <PlanCompareFooterInner onClick={onClick} data-sentry-element="PlanCompareFooterInner" data-sentry-component="PlanCompareFooter" data-sentry-source-file="GroupPricingSection.tsx" />;
const PlanCompareFooterInner = ({
  onClick
}: {
  onClick: () => void;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  return <button type="button" onClick={onClick} className="mt-auto flex items-center justify-center gap-3xs pb-md" data-sentry-component="PlanCompareFooterInner" data-sentry-source-file="GroupPricingSection.tsx">
      <span className="text-body-sm-semibold text-assistive">
        {t('common:pricing.comparePlanFeatures')}
      </span>
      <ChevronDownIcon size="sm" className="[&>*]:fill-assistive" data-sentry-element="ChevronDownIcon" data-sentry-source-file="GroupPricingSection.tsx" />
    </button>;
};
const KoreanSchoolTabContent = () => {
  const {
    openInquiryUrl
  } = useInquiryUrl({
    krUrl: '/embed/sales?type=inquiry',
    source: 'group'
  });
  const handleInquiry = () => {
    void openInquiryUrl({
      location: 'pricing_school_tab_kr',
      onInquiryClick: tracking_id => trackEvent('click_school_pro_inquiry', {
        tracking_id
      })
    });
  };
  return <div className="flex w-full flex-col gap-xl" data-sentry-component="KoreanSchoolTabContent" data-sentry-source-file="GroupPricingSection.tsx">
      <KoreanProSchoolPlan onContactClick={handleInquiry} data-sentry-element="KoreanProSchoolPlan" data-sentry-source-file="GroupPricingSection.tsx" />
      <SchoolBudgetCallout data-sentry-element="SchoolBudgetCallout" data-sentry-source-file="GroupPricingSection.tsx" />
    </div>;
};
const KoreanProSchoolPlan = ({
  onContactClick
}: {
  onContactClick: () => void;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  return <div className="flex w-full flex-col gap-xl rounded-xl border border-alternative bg-default p-xl shadow-lg tablet-lg:flex-row tablet-lg:items-stretch laptop-sm:gap-2xl laptop-sm:p-2xl" data-sentry-component="KoreanProSchoolPlan" data-sentry-source-file="GroupPricingSection.tsx">
      <div className="flex w-full flex-col justify-between gap-[28px] tablet-lg:max-w-[360px] tablet-lg:flex-1 laptop-sm:max-w-[400px]">
        <div>
          <div className="flex items-center gap-2xs pb-lg">
            <LegacyBoltSmallRectangleColorVioletIcon size="xl" data-sentry-element="LegacyBoltSmallRectangleColorVioletIcon" data-sentry-source-file="GroupPricingSection.tsx" />
            <p className="text-heading-xl-extrabold text-default">
              {t('pricing:schoolGroup.proSchoolPlan')}
            </p>
          </div>

          <div className="flex flex-col gap-3xs">
            <div className="flex items-end gap-sm">
              <p className="text-[18px] font-bold leading-[25.2px] text-assistive line-through">
                ￦50,000
              </p>
              <span className="flex min-h-[26px] items-center justify-center rounded-xs border border-pink-400/10 bg-pink-400/[0.15] px-xs text-body-sm-semibold text-pink-400">
                {t('common:pricing.schoolGroup.discount40')}
              </span>
            </div>
            <div className="flex w-full flex-wrap items-end gap-xs">
              <p className="bg-clip-text text-display-sm-extrabold text-transparent bg-gradient-violet-blue">
                ￦30,000
              </p>
              <div className="flex flex-col pb-3xs text-body-sm-medium text-alternative">
                <span>{t('pricing:schoolGroup.perTeacher')}</span>
                <span>{t('pricing:schoolGroup.perMonth')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2xl">
          <div className="flex flex-col gap-md">
            <p className="text-body-lg-medium text-assistive">
              {t('pricing:schoolGroup.purchaseCondition')}
            </p>
            <ul className="flex flex-col gap-sm">
              <li className="flex items-start gap-sm">
                <LegacyPersonStackIcon size="sm" className="shrink-0 text-strong" data-sentry-element="LegacyPersonStackIcon" data-sentry-source-file="GroupPricingSection.tsx" />
                <p className="text-body-lg-semibold text-default">
                  {t('pricing:schoolGroup.minimumFiveUsers')}
                </p>
              </li>
              <li className="flex items-start gap-sm">
                <LegacyCalendarIcon size="sm" className="shrink-0 text-strong" data-sentry-element="LegacyCalendarIcon" data-sentry-source-file="GroupPricingSection.tsx" />
                <p className="text-body-lg-semibold text-default">
                  {t('pricing:schoolGroup.minimumThreeMonths')}
                </p>
              </li>
            </ul>
          </div>

          <div className="flex flex-col items-center gap-sm">
            <Button variant="contained" intent="gradient" size="lg" className="w-full" onClick={onContactClick} data-sentry-element="Button" data-sentry-source-file="GroupPricingSection.tsx">
              <LegacyBoltFillIcon size="md" className="[&>*]:fill-white" data-sentry-element="LegacyBoltFillIcon" data-sentry-source-file="GroupPricingSection.tsx" />
              {t('pricing:schoolGroup.inquiry')}
            </Button>
            <p className="text-body-sm-medium text-alternative opacity-80">
              {t('pricing:schoolGroup.paymentNotice')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-xl rounded-md bg-neutral p-xl tablet-lg:flex-1 tablet-lg:p-2xl">
        <p className="text-heading-lg-extrabold text-default">
          <Trans t={t} i18nKey="pricing:schoolGroup.planSummary" components={{
          Strong: <span className="text-strong" />
        }} data-sentry-element="Trans" data-sentry-source-file="GroupPricingSection.tsx" />
        </p>
        <div className="flex flex-col gap-lg laptop-sm:h-[245px] laptop-sm:flex-row">
          <KoreanSchoolPlanFeatureGroup title={t('pricing:schoolGroup.allZepQuizFeatures')} features={KOREAN_SCHOOL_PLAN_FEATURES} data-sentry-element="KoreanSchoolPlanFeatureGroup" data-sentry-source-file="GroupPricingSection.tsx" />
          <KoreanSchoolPlanFeatureGroup title={t('pricing:quizAi.title')} features={KOREAN_SCHOOL_PLAN_AI_FEATURES} icon={<img className="size-lg shrink-0" src="/assets/javis_small.webp" alt="QUIZ AI" />} data-sentry-element="KoreanSchoolPlanFeatureGroup" data-sentry-source-file="GroupPricingSection.tsx" />
        </div>
      </div>
    </div>;
};
const KoreanSchoolPlanFeatureGroup = ({
  title,
  features,
  icon
}: {
  title: string;
  features: readonly (typeof KOREAN_SCHOOL_PLAN_FEATURES | typeof KOREAN_SCHOOL_PLAN_AI_FEATURES)[number][];
  icon?: ReactNode;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  return <div className="flex w-full flex-col gap-sm laptop-sm:flex-1 laptop-sm:gap-lg" data-sentry-component="KoreanSchoolPlanFeatureGroup" data-sentry-source-file="GroupPricingSection.tsx">
      <div className="flex items-center gap-2xs">
        {icon}
        <p className="text-heading-sm-bold text-default">{title}</p>
      </div>
      <ul className="flex flex-col gap-sm">
        {features.map(feature => <li key={feature} className="flex min-h-lg items-start gap-2xs">
            <CheckSmallIcon size="md" className="shrink-0 text-strong" />
            <span className="flex-1 text-body-lg-medium text-default">
              {t(feature as never, {
            defaultValue: feature === ALL_QUIZ_TYPES_FEATURE_COPY.key ? ALL_QUIZ_TYPES_FEATURE_COPY.defaultValue : undefined
          })}
            </span>
          </li>)}
      </ul>
    </div>;
};
const GlobalSchoolTabContent = ({
  basicCurrency,
  basicMetrics,
  content,
  proCurrency,
  proMetrics
}: {
  basicCurrency: string;
  basicMetrics?: GroupPricingPlanMetrics;
  content: GroupPricingContent;
  proCurrency: string;
  proMetrics?: GroupPricingPlanMetrics;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const {
    openInquiryUrl
  } = useInquiryUrl({
    krUrl: '/embed/sales?type=inquiry',
    source: 'group'
  });
  const handleInquiry = () => {
    void openInquiryUrl({
      location: 'pricing_school_tab_global',
      onInquiryClick: tracking_id => trackEvent('click_school_pro_inquiry', {
        tracking_id
      })
    });
  };

  // 우측 카드 하단 "플랜별 기능 비교" → 비교표로 스크롤(개인 탭과 동일 동작).
  const handleCompareClick = () => {
    logger.track('click_compare_plan_features_button');
    document.getElementById('plan-comparison')?.scrollIntoView({
      block: 'start',
      behavior: 'smooth'
    });
  };
  return <div className="flex w-full min-w-0 flex-col gap-lg rounded-xl border border-alternative bg-default p-lg shadow-lg tablet-sm:gap-xl tablet-sm:p-xl tablet-lg:gap-2xl tablet-lg:p-2xl laptop-sm:flex-row laptop-sm:items-stretch" data-sentry-component="GlobalSchoolTabContent" data-sentry-source-file="GroupPricingSection.tsx">
      <SchoolInfoColumn content={content} onInquiryClick={handleInquiry} data-sentry-element="SchoolInfoColumn" data-sentry-source-file="GroupPricingSection.tsx" />
      <div className="flex w-full min-w-0 flex-col gap-lg tablet-sm:flex-row tablet-sm:items-stretch laptop-sm:w-[824px] laptop-sm:shrink-0">
        <SchoolPlanCard icon={<BoltSmallRectangleColorMintIcon size="xl" />} title={t('pricing:groupPricing.default.basic.title')} discountLabel={basicMetrics?.discountPercent ? t('pricing:groupPricing.default.discount', {
        discount: basicMetrics.discountPercent
      }) : undefined} metrics={basicMetrics} plan={content.basic} currency={basicCurrency} averageTeacherCount={content.averageTeacherCount} description={t('common:pricing.basic.description')} featureSubtitle={t('common:pricing.pro.includesAllFeatures', {
        plans: 'Free'
      })} features={[t('common:planFeatures.basic.useBasicMap'), t('common:planFeatures.basic.allowPrivateQuiz')]} aiTitle="QUIZ AI" aiFeatures={[t('common:planFeatures.basic.allowBuildAiQuiz')]} onCompareClick={handleCompareClick} data-sentry-element="SchoolPlanCard" data-sentry-source-file="GroupPricingSection.tsx" />
        <SchoolPlanCard icon={<BoltSmallRectangleColorVioletIcon size="xl" />} title={t('pricing:groupPricing.default.pro.title')} discountLabel={proMetrics?.discountPercent ? t('pricing:groupPricing.default.discount', {
        discount: proMetrics.discountPercent
      }) : undefined} metrics={proMetrics} plan={content.pro} currency={proCurrency} averageTeacherCount={content.averageTeacherCount} description={t('common:pricing.pro.description')} featureSubtitle={t('common:pricing.pro.includesAllFeatures', {
        plans: 'Free, Basic'
      })} features={[t('common:planFeatures.unlimitedMapUsage'), t(ALL_QUIZ_TYPES_FEATURE_COPY.key as never, {
        defaultValue: ALL_QUIZ_TYPES_FEATURE_COPY.defaultValue
      }), t('common:planFeatures.allLearningReport'), t('common:planFeatures.continueNextQuiz'), t('common:planFeatures.saveWorksheet')]} aiTitle={t('common:pricing.pro.enhancedQuizAI')} aiFeatures={[t('common:planFeatures.aiTutor'), t('common:planFeatures.multilingualTranslation'), t('common:planFeatures.createSimilarQuestions'), t('common:planFeatures.createChoicesAndAnswers')]} onCompareClick={handleCompareClick} data-sentry-element="SchoolPlanCard" data-sentry-source-file="GroupPricingSection.tsx" />
      </div>
    </div>;
};

// 좌측 정보 컬럼: 헤더 + 조건 + "이런 학교에 추천해요" + 문의 버튼.
const SchoolInfoColumn = ({
  content,
  onInquiryClick
}: {
  content: GroupPricingContent;
  onInquiryClick: () => void;
}) => {
  const {
    t
  } = useTranslation(['pricing']);
  return <div className="flex w-full min-w-0 flex-col justify-between gap-lg tablet-sm:gap-xl laptop-sm:min-w-[256px] laptop-sm:max-w-[320px] laptop-sm:flex-1 laptop-sm:gap-2xl" data-sentry-component="SchoolInfoColumn" data-sentry-source-file="GroupPricingSection.tsx">
      <div className="flex flex-col gap-lg tablet-sm:gap-xl laptop-sm:gap-2xl">
        <div className="flex flex-col gap-sm">
          {/* 학교 아이콘 글리프를 violet→purple 대각선 그라디언트로 채움. */}
          <svg width="0" height="0" className="absolute" aria-hidden="true" data-sentry-element="svg" data-sentry-source-file="GroupPricingSection.tsx">
            <defs data-sentry-element="defs" data-sentry-source-file="GroupPricingSection.tsx">
              <linearGradient id="school-icon-gradient" x1="0" y1="0" x2="1" y2="1" data-sentry-element="linearGradient" data-sentry-source-file="GroupPricingSection.tsx">
                <stop offset="0%" stopColor="#6758ff" data-sentry-element="stop" data-sentry-source-file="GroupPricingSection.tsx" />
                <stop offset="100%" stopColor="#af70ff" data-sentry-element="stop" data-sentry-source-file="GroupPricingSection.tsx" />
              </linearGradient>
            </defs>
          </svg>
          <SchoolFillIcon className="size-[35px] mobile-lg:size-[40px] [&>*]:[fill:url(#school-icon-gradient)]" data-sentry-element="SchoolFillIcon" data-sentry-source-file="GroupPricingSection.tsx" />
          <p className="flex min-w-0 flex-wrap gap-x-2xs break-words text-heading-xl-extrabold text-default mobile-sm:text-display-sm-extrabold">
            <span className="min-w-0 laptop-sm:w-full">
              {content.title.first}
            </span>
            <span className="min-w-0 bg-clip-text text-transparent bg-gradient-violet-purple laptop-sm:w-full">
              {content.title.second}
            </span>
          </p>
        </div>
        <ul className="flex flex-col gap-md">
          <li className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-sm">
            <PersonStackIcon size="md" className="shrink-0 text-strong" data-sentry-element="PersonStackIcon" data-sentry-source-file="GroupPricingSection.tsx" />
            <span className="flex min-w-0 flex-wrap items-center gap-sm">
              <span className="min-w-0 max-w-full whitespace-normal break-words text-body-lg-bold text-default [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] tablet-sm:text-heading-sm-bold">
                {t('pricing:groupPricing.default.unlimitedUsers')}
              </span>
              <SchoolOnlyChip data-sentry-element="SchoolOnlyChip" data-sentry-source-file="GroupPricingSection.tsx" />
            </span>
          </li>
          <li className="flex items-center gap-sm">
            <CalendarIcon size="md" className="shrink-0 text-strong" data-sentry-element="CalendarIcon" data-sentry-source-file="GroupPricingSection.tsx" />
            <span className="text-body-lg-bold text-default [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] tablet-sm:text-heading-sm-bold">
              {content.periodMessage}
            </span>
          </li>
        </ul>
      </div>

      <div className="flex flex-col gap-lg tablet-sm:gap-xl laptop-sm:gap-2xl">
        <div className="flex flex-col gap-md">
          <p className="text-body-md-bold text-strong">
            {t('pricing:groupPricing.default.recommendationTitle')}
          </p>
          <ul className="flex flex-col gap-sm">
            {content.recommendations.map(item => <li key={item} className="flex items-center gap-sm">
                <CheckIcon size="2xs" className="shrink-0 [&>*]:fill-assistive" />
                <span className="min-w-0 flex-1 break-words text-body-lg-medium text-default">
                  {item}
                </span>
              </li>)}
          </ul>
        </div>
        <div className="flex flex-col items-center gap-sm">
          <ZusButton variant="contained" intent="gradient" size="lg" rounded="default" leftSlot={<BoltFillIcon size="sm" className="[&>*]:fill-white" />} className="w-full !bg-gradient-violet-purple" onClick={onInquiryClick} data-sentry-element="ZusButton" data-sentry-source-file="GroupPricingSection.tsx">
            {t('pricing:groupPricing.default.inquiryButton')}
          </ZusButton>
          <p className="text-body-sm-medium text-alternative opacity-80">
            {t('pricing:schoolGroup.vatNotice')}
          </p>
        </div>
      </div>
    </div>;
};
const SchoolOnlyChip = () => {
  const {
    t
  } = useTranslation(['pricing']);
  return <ZusChip size="sm" rounded="default" className="shrink-0 whitespace-nowrap border-violet-500/10 bg-violet-500/[0.08]" data-sentry-element="ZusChip" data-sentry-component="SchoolOnlyChip" data-sentry-source-file="GroupPricingSection.tsx">
      <span className="inline-block bg-clip-text text-body-sm-semibold text-transparent bg-gradient-violet-purple">
        {t('pricing:groupPricing.default.schoolOnlyTag')}
      </span>
    </ZusChip>;
};
interface SchoolPlanCardProps {
  icon: ReactNode;
  title: string;
  discountLabel?: string;
  metrics?: GroupPricingPlanMetrics;
  plan: GroupPricingPlan;
  currency: string;
  averageTeacherCount: number;
  description: string;
  featureSubtitle: string;
  features: string[];
  aiTitle: string;
  aiFeatures: string[];
  onCompareClick: () => void;
}

// 우측 학교 요금제 카드(Basic/Pro). 공용 PricingCard 프리미티브로 조립.
const SchoolPlanCard = ({
  icon,
  title,
  discountLabel,
  metrics,
  plan,
  currency,
  averageTeacherCount,
  description,
  featureSubtitle,
  features,
  aiTitle,
  aiFeatures,
  onCompareClick
}: SchoolPlanCardProps) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  return <PricingCard.Container className="min-w-0 tablet-sm:flex-1" data-sentry-element="unknown" data-sentry-component="SchoolPlanCard" data-sentry-source-file="GroupPricingSection.tsx">
      <PricingCard.TopSection data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx">
        <PricingCard.Header data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx">
          {icon}
          <h2 className="text-heading-lg-extrabold text-default tablet-lg:text-heading-xl-extrabold">
            {title}
          </h2>
        </PricingCard.Header>
        <SchoolPriceBlock averageTeacherCount={averageTeacherCount} currency={currency} discountLabel={discountLabel} metrics={metrics} plan={plan} data-sentry-element="SchoolPriceBlock" data-sentry-source-file="GroupPricingSection.tsx" />
        <PricingCard.Description data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx">{description}</PricingCard.Description>
      </PricingCard.TopSection>

      <PricingCard.Divider data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx" />

      <PricingCard.BottomSection data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx">
        <PricingCard.FeatureTitles mainTitle={t('common:pricing.pro.usefulFeatures')} subTitle={featureSubtitle} data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx" />
        <PricingCard.FeatureItemList items={features} data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx" />
        <PricingCard.AiFeatureItemList title={aiTitle} items={aiFeatures} data-sentry-element="unknown" data-sentry-source-file="GroupPricingSection.tsx" />
      </PricingCard.BottomSection>

      <PlanCompareFooter onClick={onCompareClick} data-sentry-element="PlanCompareFooter" data-sentry-source-file="GroupPricingSection.tsx" />
    </PricingCard.Container>;
};

// 학교 요금제 가격 블록. 금액이 없으면 카드 구조를 유지하고 문의 문구만 표시한다.
const SchoolPriceBlock = ({
  averageTeacherCount,
  currency,
  discountLabel,
  metrics,
  plan
}: {
  averageTeacherCount: number;
  currency: string;
  discountLabel?: string;
  metrics?: GroupPricingPlanMetrics;
  plan: GroupPricingPlan;
}) => {
  const {
    t,
    i18n
  } = useTranslation(['pricing']);
  const displayCurrency = formatCurrencySymbol(currency);
  const annualTotalPrice = metrics?.annualTotalPrice.toLocaleString(i18n.language, {
    maximumFractionDigits: 2
  });
  const monthlyPricePerPerson = metrics?.monthlyPricePerPerson.toLocaleString(i18n.language, {
    maximumFractionDigits: 2
  });
  if (plan.monthlyPricePerPerson.type === 'inquiry') {
    return <strong className="text-display-sm-extrabold text-default">
        {t('pricing:groupPricing.default.priceInquiry')}
      </strong>;
  }
  return <div className="flex flex-col gap-sm" data-sentry-component="SchoolPriceBlock" data-sentry-source-file="GroupPricingSection.tsx">
      {metrics && <div className="flex min-w-0 flex-wrap items-center gap-xs">
          <span className="flex min-w-0 max-w-full flex-wrap items-center gap-x-4xs gap-y-2xs">
            <span className="min-w-0 break-words text-body-md-bold text-default">
              {t('pricing:groupPricing.default.perPerson')}
            </span>
            <span className="min-w-0 break-words text-body-md-bold text-strong">
              {t('pricing:groupPricing.default.monthlyPrice', {
            currency: displayCurrency,
            price: monthlyPricePerPerson
          })}
            </span>
            <span className="min-w-0 break-words text-body-md-medium text-alternative">
              {t('pricing:groupPricing.default.teacherCountBasis', {
            count: averageTeacherCount
          })}
            </span>
          </span>
          {discountLabel && <ZusChip size="md" rounded="default" className="shrink-0 whitespace-nowrap border-pink-400/10 bg-pink-400/[0.15] text-pink-400">
              {discountLabel}
            </ZusChip>}
        </div>}
      <div className="flex flex-wrap items-end gap-xs">
        <strong className="text-display-sm-extrabold text-default">
          {annualTotalPrice}
        </strong>
        <span className="flex flex-col pb-3xs text-body-sm-medium text-alternative">
          <span>{displayCurrency}</span>
          <span>{t('pricing:groupPricing.default.annualSuffix')}</span>
        </span>
      </div>
    </div>;
};
const SchoolBudgetCallout = () => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const {
    openInquiryUrl
  } = useInquiryUrl({
    krUrl: '/embed/sales?type=inquiry',
    source: 'pricing'
  });
  const handleInquiry = () => {
    void openInquiryUrl({
      location: 'pricing_school_budget_callout',
      onInquiryClick: tracking_id => trackEvent('click_school_budget_inquiry', {
        tracking_id
      })
    });
  };
  return <div className="flex w-full flex-col items-start overflow-clip rounded-lg border-2 border-strong-assistive bg-default p-lg tablet-sm:px-2xl tablet-sm:py-xl" data-sentry-component="SchoolBudgetCallout" data-sentry-source-file="GroupPricingSection.tsx">
      <div className="flex w-full flex-col items-start gap-xl tablet-sm:flex-row tablet-sm:items-center tablet-sm:gap-2xl">
        <div className="flex min-w-0 flex-1 flex-col items-start gap-md">
          <div className="flex h-xl w-full items-center gap-xs">
            <SchoolFillIcon size="sm" className="shrink-0 text-strong tablet-sm:size-7" data-sentry-element="SchoolFillIcon" data-sentry-source-file="GroupPricingSection.tsx" />
            <h3 className="min-w-0 flex-1 text-heading-md-bold text-default tablet-sm:text-heading-lg-extrabold">
              <Trans t={t} i18nKey="pricing:schoolBudget.title" components={{
              Strong: <span className="text-strong" />
            }} data-sentry-element="Trans" data-sentry-source-file="GroupPricingSection.tsx" />
            </h3>
          </div>

          <div className="flex w-full flex-wrap content-center items-center gap-2xs">
            {BUDGET_TAGS.map(label => <ZusChip key={label} size="lg" rounded="full" className="min-h-[32px] border-assistive bg-opacity-black-1 text-neutral">
                {label}
              </ZusChip>)}
          </div>
        </div>

        <ZusButton variant="contained" intent="alternative" size="md" rightSlot={<ChevronRightIcon size="sm" />} className="w-full shrink-0 tablet-sm:w-auto" onClick={handleInquiry} data-sentry-element="ZusButton" data-sentry-source-file="GroupPricingSection.tsx">
          {t('pricing:schoolBudget.inquiry')}
        </ZusButton>
      </div>
    </div>;
};